// SEO text of a venue (brand) page, /book/<brand>: the <title> and description (SEO sheet 2026-10-20 item 4), photo
// alt text (item 8), the address in the page's language (item 9) and the Restaurant / CafeOrCoffeeShop JSON-LD
// (item 6). Pure functions over GET /physical-spaces rows, so the server page and the client page agree.
// An entertainment centre (My Town, Snow City…) is booked as a party room: its own title, a per-room price and
// EntertainmentBusiness schema (SEO 2026-11-02 item 4).
import type { SpaceRow } from '@/lib/v9/brands';

export type Lang = 'ar' | 'en';
type Row = Partial<SpaceRow> & Record<string, unknown>;

const ARABIC = /[؀-ۿ]/;
const clean = (v: unknown) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

/** The value of a pair written in the page's language: the admin sometimes swaps the Arabic and English boxes. */
function inLang(lang: Lang, ...candidates: unknown[]): string {
  const values = candidates.map(clean).filter(Boolean);
  return values.find((v) => (lang === 'ar' ? ARABIC.test(v) : !ARABIC.test(v))) ?? '';
}

/** Cuisine ("لبناني" / "Lebanese"): the branch's area field. */
export const cuisineOf = (row: Row | null | undefined, lang: Lang) => (row ? inLang(lang, row.area_ar, row.area) : '');
/** District (neighbourhood), set in the admin. */
export const districtOf = (row: Row | null | undefined, lang: Lang) => (row ? inLang(lang, row.district_ar, row.district_en) : '');
export const cityOf = (row: Row | null | undefined, lang: Lang) => (row ? inLang(lang, row.city_name_ar, row.city_name_en) : '');

export type VenueKind = 'restaurant' | 'cafe' | 'entertainment' | 'other';
export function kindWord(kind: VenueKind, lang: Lang) {
  if (kind === 'cafe') return lang === 'ar' ? 'كافيه' : 'Café';
  return lang === 'ar' ? 'مطعم' : 'Restaurant';
}

/** Alt text of a venue photo: "{name} في حي {district}، {city}" / "{name} in {district}, {city}". */
export function venueAlt(name: string, district: string, city: string, lang: Lang): string {
  if (lang === 'ar') return district ? `${name} في حي ${district}${city ? `، ${city}` : ''}` : [name, city].filter(Boolean).join('، ');
  return district ? `${name} in ${district}${city ? `, ${city}` : ''}` : [name, city].filter(Boolean).join(', ');
}

/** Alt text of a seating photo: "{name}، {seating}" / "{name}, {seating}" (e.g. «باغاتيل، جلسة خارجية»). */
export function seatingAlt(name: string, seating: string, lang: Lang): string {
  const s = clean(seating);
  return s ? `${name}${lang === 'ar' ? '، ' : ', '}${s}` : name;
}

/**
 * One alt text per gallery photo, none repeated (SEO 2026-11-02 item 3). A seating's photo names the seating; the
 * branch's own photos have no label in the data, so they read as the place (venueAlt) and a repeat is numbered.
 */
export function galleryAlts(photos: { seating?: string | null }[], name: string, place: string, lang: Lang): string[] {
  const seen = new Map<string, number>();
  return photos.map((p) => {
    const base = p.seating ? seatingAlt(name, p.seating, lang) : place;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return n === 1 ? base : `${base}${lang === 'ar' ? `، صورة ${n}` : `, photo ${n}`}`;
  });
}

/** The branch's address in the page's language: the Arabic address, else district + city, else what was entered. */
export function venueAddress(row: Row | null | undefined, lang: Lang): string {
  if (!row) return '';
  const raw = clean(row.address);
  if (lang === 'ar') {
    const ar = clean(row.address_ar);
    if (ar) return ar;
    const district = districtOf(row, 'ar');
    const city = cityOf(row, 'ar');
    if (district) return [`حي ${district}`, city].filter(Boolean).join('، ');
    return raw;
  }
  return raw || [districtOf(row, 'en'), cityOf(row, 'en')].filter(Boolean).join(', ');
}

export type VenueFacts = {
  name: string;
  kind: VenueKind;
  city: string;
  cuisine: string;
  district: string;
  /** Cheapest booking price (after discount), SAR. */
  price: number | null;
  /** Per person (a table) or per room (a whole room). */
  perRoom: boolean;
  /** The booking value is taken off the restaurant bill (said in the branch's pricing details). */
  deducted: boolean;
};

const fmtPrice = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** حجز {مطعم|كافيه} {الاسم} {المدينة} | {المطبخ} في حي {الحي} | ميلا; an entertainment centre: حجز غرفة حفلة عيد ميلاد في {الاسم} {المدينة} | ميلا */
export function venueTitle(f: VenueFacts, lang: Lang): string {
  if (f.kind === 'entertainment') {
    return lang === 'ar'
      ? `حجز غرفة حفلة عيد ميلاد في ${f.name}${f.city ? ` ${f.city}` : ''} | ميلا`
      : `Book a Birthday Party Room at ${f.name}${f.city ? ` ${f.city}` : ''} | Mila`;
  }
  if (lang === 'ar') {
    const second = f.cuisine && f.district ? `${f.cuisine} في حي ${f.district}` : f.cuisine || (f.district ? `حي ${f.district}` : '');
    return [`حجز ${kindWord(f.kind, 'ar')} ${f.name}${f.city ? ` ${f.city}` : ''}`, second, 'ميلا'].filter(Boolean).join(' | ');
  }
  const second = f.cuisine && f.district ? `${f.cuisine} in ${f.district}` : f.cuisine || f.district;
  return [`Book ${f.name} ${kindWord(f.kind, 'en')}${f.city ? ` ${f.city}` : ''}`, second, 'Mila'].filter(Boolean).join(' | ');
}

/** احجز طاولتك في {الاسم} ({الحي}، {المدينة}) من ميلا. قيمة الحجز من {السعر} ريال للشخص وتخصم من الفاتورة. أضف الكيك والورد والديكور لمناسبتك بنفس الحجز. */
export function venueDescription(f: VenueFacts, lang: Lang): string {
  if (lang === 'ar') {
    const where = [f.district, f.city].filter(Boolean).join('، ');
    const what = f.kind === 'entertainment' ? 'احجز غرفة احتفال' : f.perRoom ? 'احجز مكانك' : 'احجز طاولتك';
    const price = f.price != null
      ? ` قيمة الحجز من ${fmtPrice(f.price)} ريال ${f.perRoom ? 'للغرفة' : 'للشخص'}${f.deducted ? ' وتخصم من الفاتورة' : ''}.`
      : '';
    return `${what} في ${f.name}${where ? ` (${where})` : ''} من ميلا.${price} أضف الكيك والورد والديكور لمناسبتك بنفس الحجز.`;
  }
  const where = [f.district, f.city].filter(Boolean).join(', ');
  const what = f.kind === 'entertainment' ? 'Book a party room' : f.perRoom ? 'Book your room' : 'Book your table';
  const price = f.price != null
    ? ` Booking from SAR ${fmtPrice(f.price)} ${f.perRoom ? 'per room' : 'per person'}${f.deducted ? ', taken off your bill' : ''}.`
    : '';
  return `${what} at ${f.name}${where ? ` (${where})` : ''} with Mila.${price} Add cake, flowers and decor for your occasion in the same booking.`;
}

/** The pricing details say the booking value comes off the bill ("يتم خصمها من إجمالي قيمة الطلب" / "deducted"). */
export function bookingDeducted(row: Row | null | undefined): boolean {
  const text = `${clean(row?.pricing_details_ar)} ${clean(row?.pricing_details_en)}`;
  return /خصم|تخصم|يخصم|deduct|taken off|applied toward/i.test(text);
}

export type SeatingFlags = { privateRoom: boolean; outdoor: boolean; family: boolean };
export function seatingFlags(row: Row | null | undefined): SeatingFlags {
  return { privateRoom: row?.has_private_room === true, outdoor: row?.has_outdoor_seating === true, family: row?.has_family_section === true };
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : null;
};

export type VenueSchemaInput = {
  facts: { ar: VenueFacts; en: VenueFacts };
  lang: Lang;
  url: string;
  /** Absolute, permanent image URLs (lib/seo/media.ts mediaUrl). */
  images: string[];
  branch: Row | null;
  /** Real ratings from Mila bookings only; no rating, no aggregateRating. */
  rating: { value: number; count: number } | null;
  /** Real reviews (rating + comment) from Mila bookings, newest first; first names only. */
  reviews?: { rating: number; comment?: string | null; author?: string | null; date?: string | null }[];
  description?: string;
  breadcrumbs: { name: string; url: string }[];
};

const SCHEMA_TYPE: Record<VenueKind, string> = { restaurant: 'Restaurant', cafe: 'CafeOrCoffeeShop', entertainment: 'EntertainmentBusiness', other: 'Restaurant' };

/** Restaurant / CafeOrCoffeeShop / EntertainmentBusiness with a ReserveAction, plus its BreadcrumbList (schema.org JSON-LD). */
export function venueJsonLd(i: VenueSchemaInput): Record<string, unknown>[] {
  const f = i.facts[i.lang];
  const other = i.facts[i.lang === 'ar' ? 'en' : 'ar'];
  const lat = num(i.branch?.latitude);
  const lng = num(i.branch?.longitude);
  const street = venueAddress(i.branch, i.lang);
  const food = f.kind !== 'entertainment';
  const venue: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': SCHEMA_TYPE[f.kind],
    '@id': `${i.url}#venue`,
    name: f.name,
    ...(other.name && other.name !== f.name ? { alternateName: other.name } : {}),
    url: i.url,
    ...(i.description ? { description: i.description } : {}),
    ...(i.images.length ? { image: i.images.slice(0, 3) } : {}),
    address: {
      '@type': 'PostalAddress',
      ...(street ? { streetAddress: street } : {}),
      ...(f.district ? { addressRegion: f.district } : {}),
      ...(f.city ? { addressLocality: f.city } : {}),
      addressCountry: 'SA',
    },
    ...(lat != null && lng != null ? { geo: { '@type': 'GeoCoordinates', latitude: lat, longitude: lng } } : {}),
    // servesCuisine and acceptsReservations belong to a food establishment (a party room's "area" is not a cuisine).
    ...(food && f.cuisine ? { servesCuisine: f.cuisine.split(/[,،]\s*/).filter(Boolean) } : {}),
    ...(f.price != null ? { priceRange: `${fmtPrice(f.price)} SAR` } : {}),
    ...(food ? { acceptsReservations: true } : {}),
    potentialAction: {
      '@type': 'ReserveAction',
      target: { '@type': 'EntryPoint', urlTemplate: i.url, inLanguage: i.lang, actionPlatform: ['https://schema.org/DesktopWebPlatform', 'https://schema.org/MobileWebPlatform'] },
      result: { '@type': food ? 'FoodEstablishmentReservation' : 'Reservation', name: i.lang === 'ar' ? `حجز ${f.name}` : `Booking at ${f.name}` },
    },
    ...(i.rating && i.rating.count > 0 && i.rating.value > 0
      ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: Math.round(i.rating.value * 10) / 10, reviewCount: i.rating.count, bestRating: 5, worstRating: 1 } }
      : {}),
    ...(i.rating && i.rating.count > 0 && i.reviews?.some((r) => clean(r.comment))
      ? {
          review: i.reviews.filter((r) => clean(r.comment) && r.rating > 0).slice(0, 5).map((r) => ({
            '@type': 'Review',
            reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
            reviewBody: clean(r.comment),
            author: { '@type': 'Person', name: clean(r.author) || (i.lang === 'ar' ? 'ضيف ميلا' : 'Mila guest') },
            ...(r.date ? { datePublished: r.date.slice(0, 10) } : {}),
          })),
        }
      : {}),
  };
  const crumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: i.breadcrumbs.map((b, n) => ({ '@type': 'ListItem', position: n + 1, name: b.name, item: b.url })),
  };
  return [venue, crumbs];
}

/** Everything the title / description / schema need, from the brand's branches (the first branch leads). */
export function venueFacts(
  { name, kind, price, perRoom, branches }: { name: { ar: string; en: string }; kind: VenueKind; price: number | null; perRoom: boolean; branches: Row[] },
  lang: Lang,
): VenueFacts {
  const first = branches[0] ?? null;
  // A district names one place: only when the brand has a single branch.
  const district = branches.length === 1 ? districtOf(first, lang) : '';
  return {
    name: lang === 'ar' ? name.ar || name.en : name.en || name.ar,
    kind,
    city: cityOf(first, lang),
    // An entertainment centre's "area" is "غرفة احتفال", not a cuisine.
    cuisine: kind === 'entertainment' ? '' : cuisineOf(first, lang),
    district,
    price,
    // Its rooms are booked whole and priced per room.
    perRoom: perRoom || kind === 'entertainment',
    deducted: bookingDeducted(first),
  };
}
